import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Pencil, Trash2, ArrowUp, ArrowDown, Save, Copy, X,
} from "lucide-react";
import { toast } from "sonner";
import {
  UNITS, UNIT_SHORT, AGGREGATIONS, CATEGORY_KEYS, CATEGORY_LABELS,
} from "@/lib/physical";

const emptyDef = () => ({
  name: "", description: "", unit: "s", direction: "lower",
  attempts: 1, aggregation: "best", categories: [], active: true, baremos: {},
});

export default function TestDefsManager({ open, onClose, onChanged }) {
  const [defs, setDefs] = useState([]);
  const [editing, setEditing] = useState(null);     // def object or null
  const [confirmDel, setConfirmDel] = useState(null);
  const [copyOpen, setCopyOpen] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/physical-tests/defs?include_inactive=true");
      setDefs(r.data || []);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const persistOrder = async (ids) => {
    try {
      await api.post("/physical-tests/defs/reorder", { ids });
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const move = (idx, dir) => {
    const next = [...defs];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setDefs(next);
    persistOrder(next.map((d) => d.id));
  };

  const toggleActive = async (d) => {
    try {
      await api.put(`/physical-tests/defs/${d.id}`, { ...d, active: !d.active });
      await load();
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/physical-tests/defs/${confirmDel.id}`);
      toast.success("Prueba eliminada");
      setConfirmDel(null);
      await load();
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); setConfirmDel(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="test-defs-manager">
        <DialogHeader>
          <DialogTitle>Gestión de pruebas físicas</DialogTitle>
          <DialogDescription className="text-xs">
            Crea, edita, activa/desactiva, reordena y copia baterías de pruebas por categoría.
            Las pruebas desactivadas conservan todos los resultados históricos.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 mb-2">
          <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={() => setEditing(emptyDef())} data-testid="test-def-new">
            <Plus className="h-4 w-4 mr-1.5" /> Nueva prueba
          </Button>
          <Button size="sm" variant="outline" onClick={() => setCopyOpen(true)} data-testid="test-def-copy-open">
            <Copy className="h-4 w-4 mr-1.5" /> Copiar batería entre categorías
          </Button>
        </div>

        <div className="space-y-2">
          {defs.length === 0 && (
            <div className="py-6 text-sm text-slate-400 italic">No hay pruebas definidas.</div>
          )}
          {defs.map((d, i) => (
            <div key={d.id}
              className={`border rounded-lg p-3 ${d.active ? "border-slate-200" : "border-dashed border-slate-300 bg-slate-50 opacity-70"}`}
              data-testid={`test-def-row-${d.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-slate-800">{d.name}</span>
                    <Badge variant="outline" className="text-[10px]">{UNIT_SHORT[d.unit] || d.unit}</Badge>
                    <span className="text-[11px] text-slate-500">
                      {d.direction === "lower" ? "menor mejor" : "mayor mejor"} · {d.attempts} intento(s) · {AGGREGATIONS.find((a) => a.value === d.aggregation)?.label}
                    </span>
                    {!d.active && <Badge className="bg-slate-400 text-white text-[10px]">Inactiva</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(d.categories || []).map((c) => (
                      <Badge key={c} variant="secondary" className="text-[10px]">{CATEGORY_LABELS[c] || c}</Badge>
                    ))}
                    {(d.categories || []).length === 0 && (
                      <span className="text-[11px] text-rose-500 italic">Sin categorías asignadas</span>
                    )}
                  </div>
                  {d.description && <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{d.description}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => move(i, -1)} disabled={i === 0}
                    className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`test-def-up-${d.id}`}>
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === defs.length - 1}
                    className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`test-def-down-${d.id}`}>
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <Switch checked={d.active} onCheckedChange={() => toggleActive(d)}
                    data-testid={`test-def-active-${d.id}`} />
                  <button onClick={() => setEditing({ ...d })}
                    className="p-1.5 rounded hover:bg-purple-50 text-purple-600" data-testid={`test-def-edit-${d.id}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => setConfirmDel(d)}
                    className="p-1.5 rounded hover:bg-rose-50 text-rose-500" data-testid={`test-def-del-${d.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>

      {editing && (
        <DefEditor
          def={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); onChanged?.(); }}
        />
      )}

      {copyOpen && (
        <CopyBatteryDialog
          onClose={() => setCopyOpen(false)}
          onCopied={async () => { setCopyOpen(false); await load(); onChanged?.(); }}
        />
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar prueba</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar «{confirmDel?.name}»? Si tiene resultados registrados no se podrá
              borrar; desactívala para conservar el histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={doDelete}
              data-testid="test-def-del-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

function DefEditor({ def, onClose, onSaved }) {
  const [d, setD] = useState(() => JSON.parse(JSON.stringify(def)));
  const isNew = !d.id;

  const setField = (k, v) => setD((x) => ({ ...x, [k]: v }));

  const toggleCat = (c) => setD((x) => {
    const has = (x.categories || []).includes(c);
    const categories = has ? x.categories.filter((k) => k !== c) : [...(x.categories || []), c];
    const baremos = { ...(x.baremos || {}) };
    if (!has && !baremos[c]) baremos[c] = { excelente: 0, notable: 0, bien: 0 };
    return { ...x, categories, baremos };
  });

  const setBaremo = (c, k, v) => setD((x) => ({
    ...x,
    baremos: { ...(x.baremos || {}), [c]: { ...((x.baremos || {})[c] || {}), [k]: v === "" ? 0 : Number(v) } },
  }));

  const save = async () => {
    if (!d.name.trim()) { toast.error("La prueba necesita un nombre"); return; }
    try {
      const payload = {
        name: d.name.trim(), description: d.description || "", unit: d.unit,
        direction: d.direction, attempts: Number(d.attempts) || 1,
        aggregation: d.aggregation, categories: d.categories || [],
        active: d.active !== false, baremos: d.baremos || {},
      };
      if (isNew) await api.post("/physical-tests/defs", payload);
      else await api.put(`/physical-tests/defs/${d.id}`, payload);
      toast.success(isNew ? "Prueba creada" : "Prueba actualizada");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="test-def-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nueva prueba" : "Editar prueba"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nombre del test</Label>
            <Input value={d.name} onChange={(e) => setField("name", e.target.value)}
              placeholder="ej. Sprint 20m" data-testid="def-name" />
          </div>
          <div>
            <Label className="text-xs">Descripción e instrucciones</Label>
            <Textarea value={d.description} onChange={(e) => setField("description", e.target.value)}
              placeholder="Cómo se realiza la prueba..." rows={2} data-testid="def-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Unidad de medida</Label>
              <Select value={d.unit} onValueChange={(v) => setField("unit", v)}>
                <SelectTrigger data-testid="def-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Criterio de evaluación</Label>
              <Select value={d.direction} onValueChange={(v) => setField("direction", v)}>
                <SelectTrigger data-testid="def-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lower">Menor resultado es mejor</SelectItem>
                  <SelectItem value="higher">Mayor resultado es mejor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Nº de intentos permitidos</Label>
              <Input type="number" min={1} max={10} value={d.attempts}
                onChange={(e) => setField("attempts", e.target.value)} data-testid="def-attempts" />
            </div>
            <div>
              <Label className="text-xs">Resultado para clasificar</Label>
              <Select value={d.aggregation} onValueChange={(v) => setField("aggregation", v)}>
                <SelectTrigger data-testid="def-aggregation"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AGGREGATIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Categorías en las que se aplica</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {CATEGORY_KEYS.map((c) => {
                const on = (d.categories || []).includes(c);
                return (
                  <button key={c} type="button" onClick={() => toggleCat(c)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition ${on ? "bg-purple-600 border-purple-600 text-white" : "border-slate-300 text-slate-600 hover:border-purple-300"}`}
                    data-testid={`def-cat-${c}`}>
                    {CATEGORY_LABELS[c]}
                  </button>
                );
              })}
            </div>
          </div>

          {(d.categories || []).length > 0 && (
            <div>
              <Label className="text-xs">Baremos por categoría (umbrales)</Label>
              <p className="text-[11px] text-slate-400 mb-1">
                {d.direction === "lower" ? "Menor o igual que el umbral clasifica en esa banda." : "Mayor o igual que el umbral clasifica en esa banda."}
              </p>
              <div className="space-y-2">
                {(d.categories || []).map((c) => {
                  const b = (d.baremos || {})[c] || { excelente: 0, notable: 0, bien: 0 };
                  return (
                    <div key={c} className="border border-slate-200 rounded p-2">
                      <div className="text-xs font-bold text-slate-700 mb-1">{CATEGORY_LABELS[c] || c}</div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[10px] text-emerald-700 font-bold">Excelente</Label>
                          <Input type="number" step="0.01" value={b.excelente ?? 0}
                            onChange={(e) => setBaremo(c, "excelente", e.target.value)}
                            data-testid={`def-baremo-${c}-excelente`} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-blue-700 font-bold">Notable</Label>
                          <Input type="number" step="0.01" value={b.notable ?? 0}
                            onChange={(e) => setBaremo(c, "notable", e.target.value)}
                            data-testid={`def-baremo-${c}-notable`} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-amber-700 font-bold">Bien</Label>
                          <Input type="number" step="0.01" value={b.bien ?? 0}
                            onChange={(e) => setBaremo(c, "bien", e.target.value)}
                            data-testid={`def-baremo-${c}-bien`} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Switch checked={d.active !== false} onCheckedChange={(v) => setField("active", v)} data-testid="def-active" />
            <span className="text-sm">Prueba activa</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}><X className="h-4 w-4 mr-1" /> Cancelar</Button>
          <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={save} data-testid="def-save">
            <Save className="h-4 w-4 mr-1.5" /> Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CopyBatteryDialog({ onClose, onCopied }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const run = async () => {
    if (!from || !to) { toast.error("Elige origen y destino"); return; }
    if (from === to) { toast.error("Origen y destino deben ser distintos"); return; }
    try {
      const r = await api.post("/physical-tests/defs/copy", { from_category: from, to_category: to });
      toast.success(`Batería copiada (${r.data.copied} prueba/s)`);
      onCopied();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="copy-battery-dialog">
        <DialogHeader>
          <DialogTitle>Copiar batería entre categorías</DialogTitle>
          <DialogDescription className="text-xs">
            Añade todas las pruebas (y sus baremos) de una categoría a otra como punto de partida editable.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Desde</Label>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger data-testid="copy-from"><SelectValue placeholder="Categoría origen" /></SelectTrigger>
              <SelectContent>
                {CATEGORY_KEYS.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Hacia</Label>
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger data-testid="copy-to"><SelectValue placeholder="Categoría destino" /></SelectTrigger>
              <SelectContent>
                {CATEGORY_KEYS.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={run} data-testid="copy-run">
            <Copy className="h-4 w-4 mr-1.5" /> Copiar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
